import { startCase } from 'lodash';
import { ResponseBody } from '@opensearch-project/opensearch/api/_types/_core.search';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInDays,
  differenceInHours,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  getDate,
  getDay,
  getMonth,
  isAfter,
  isBefore,
  setDate,
  setDay,
  setMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import { format, fromZonedTime, toZonedTime } from 'date-fns-tz';

import { PLACEHOLDER } from '@/constants';
import { IStoreMetadata } from '@/data';
import {
  Asset,
  CannabisType,
  Component,
  Discount,
  FulfillmentType,
  GProduct,
  LabResult,
  OpenSearchDiscountCondition,
  OpenSearchProduct,
  OpenSearchProductDiscount,
  OpenSearchProductLabResult,
  OpenSearchProductPricing,
  OpenSearchScheduleCondition,
  OpenSearchTierDetail,
  PriceType,
  Pricing,
  TierMethod,
  Variant,
} from '@/types';

import { resolveTimeZone } from './timezone';
import { compareProductSiblings } from './utils';

import data from '@/public/ssr-cache.json';

const PRODUCT_URL_PATH = '/product/';

const computeDiscountData = ({
  product,
  timeZone,
  fulfillmentType,
}: {
  product: OpenSearchProduct;
  timeZone: string;
  fulfillmentType?: FulfillmentType;
}): {
  mappedDiscounts: Discount[];
  appliedDiscountIds: string[];
  discountMethod: string;
  minDiscountedPrice: number;
  discountAmount: number;
  discountPercent: number;
} => {
  const mappedDiscounts = mapDiscounts(
    filterDiscountsOutOfScheduleAndByFulfillmentType({
      discounts: product?.productData?.discounts,
      timeZone,
      fulfillmentType,
    }),
  );

  const isTierType = product?.productData?.pricing?.priceType === 'TIER';
  const firstTierValue =
    product?.productData?.pricing?.tierDetails?.[0].pricePerValue ?? 0;
  const priceSellValue = product?.productData?.pricing?.priceSell ?? 0;
  const priceSell = isTierType ? firstTierValue : priceSellValue;

  let minDiscountedPrice = priceSell;
  let stackableDiscountsSum = 0;
  const stackableDiscountIds: string[] = [];
  let bestNonStackable: { id: string; price: number; method: string } | null =
    null;

  for (const discount of mappedDiscounts) {
    if (discount.discount_affinity === 'Pre-Cart') {
      const isStackable = discount.discount_stackable === 'Yes';
      let calculatedDiscountAmount = 0;

      if (discount.discount_method === 'PERCENT') {
        const percent = discount.discount_amount ?? 0;
        calculatedDiscountAmount = (percent / 100) * priceSell;
      } else if (discount.discount_method === 'DOLLAR') {
        calculatedDiscountAmount = discount.discount_amount ?? 0;
      } else if (discount.discount_method === 'TARGET_PRICE') {
        calculatedDiscountAmount = discount.discount_amount
          ? priceSell - discount.discount_amount
          : 0;
      }

      const discountedPrice = priceSell - calculatedDiscountAmount;
      minDiscountedPrice = Math.min(minDiscountedPrice, discountedPrice);

      if (isStackable) {
        stackableDiscountsSum += calculatedDiscountAmount;
        stackableDiscountIds.push(discount.discount_id);
      } else if (
        !bestNonStackable ||
        discountedPrice < bestNonStackable.price
      ) {
        bestNonStackable = {
          id: discount.discount_id,
          price: discountedPrice,
          method: discount.discount_method,
        };
      }
    }
  }

  const stackableDiscountedPrice = priceSell - stackableDiscountsSum;
  minDiscountedPrice = Math.min(minDiscountedPrice, stackableDiscountedPrice);

  let appliedDiscountIds: string[] = stackableDiscountIds;
  let discountMethod = '';

  if (bestNonStackable && bestNonStackable.price < stackableDiscountedPrice) {
    appliedDiscountIds = [bestNonStackable.id];
    discountMethod = bestNonStackable.method;
  } else if (stackableDiscountIds.length) {
    discountMethod =
      mappedDiscounts.find(d => d.discount_id === stackableDiscountIds[0])
        ?.discount_method ?? '';
  }

  const discountAmount = priceSell - minDiscountedPrice;
  const discountPercent = priceSell ? (discountAmount / priceSell) * 100 : 0;

  return {
    mappedDiscounts,
    appliedDiscountIds,
    discountMethod,
    minDiscountedPrice,
    discountAmount,
    discountPercent,
  };
};

const formatProductTitle = (title: string): string => {
  return title
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const getLabValue = (v?: string): Component => {
  let percentage = 0;
  let amount = 0;

  if (!v) {
    return { percentage, amount };
  }

  if (v.includes('%')) {
    percentage = parseFloat(v);
  }

  if (v?.toLowerCase()?.includes('mg')) {
    amount = parseFloat(v);
  }

  return { percentage, amount };
};

const mapProduct = ({
  product,
  isSortingApplied = false,
  isSearchingApplied = false,
  timeZone = resolveTimeZone(),
  fulfillmentType,
}: {
  product: OpenSearchProduct;
  isSortingApplied?: boolean;
  isSearchingApplied?: boolean;
  /** Store timezone used to evaluate discount schedules; defaults to the default store's. */
  timeZone?: string;
  fulfillmentType?: FulfillmentType;
}): GProduct => {
  const assets = mapPhotoAssets(product);
  const trezPriceType = priceType(product);

  const inStock = (product?.availableUnits ?? 0) > 0;
  const outStock = (product?.availableUnits ?? 0) === 0;

  /**
   * =====================================================================================
   * DISCOUNT CALCULATION AND PRICING LOGIC
   * =====================================================================================
   *
   * This section handles the complex discount calculation logic for cannabis products.
   * It processes multiple types of discounts and applies them to determine the final
   * discounted price, amount, and percentage for each product.
   *
   * PROCESS OVERVIEW:
   * 1. Filter and map discounts that are currently active based on schedule conditions
   * 2. Calculate the minimum possible discounted price from all applicable pre-cart discounts
   * 3. Compare with existing discounted price and take the better deal for the customer
   * 4. Update the product's pricing data with calculated discount information
   *
   * DISCOUNT TYPES HANDLED:
   * - PERCENT: Percentage-based discounts (e.g., 20% off)
   * - DOLLAR: Fixed dollar amount discounts (e.g., $5 off)
   * - TARGET_PRICE: Fixed target price discounts (e.g., $10 PRICE)
   *
   * DISCOUNT AFFINITY:
   * - 'Pre-Cart': Discounts applied before items are added to cart (automatic)
   * - 'Cart': Discounts applied during checkout process (may require codes/conditions)
   *
   * NOTE: Only 'Pre-Cart' discounts are processed here as they represent automatic
   * discounts that should be immediately visible to customers browsing products.
   * =====================================================================================
   */
  const {
    mappedDiscounts,
    appliedDiscountIds,
    discountMethod,
    minDiscountedPrice,
    discountAmount,
    discountPercent,
  } = computeDiscountData({ product, timeZone, fulfillmentType });

  // Apply the best available discount and update product pricing data
  if (product.productData?.pricing) {
    product.productData.pricing.discountedPrice = minDiscountedPrice;
    product.productData.pricing.discountAmount = discountAmount;
    product.productData.pricing.discountPercent = discountPercent;
    // Store applied discount IDs so UI components know which discounts actually affect this product
    product.productData.pricing.appliedDiscountIds = appliedDiscountIds;
    product.productData.pricing.discountMethod = discountMethod;
  }

  const mappedSiblings = mapSiblings(
    timeZone,
    product.siblings,
    fulfillmentType,
  );
  const sortedSiblings = mappedSiblings?.sort(compareProductSiblings);
  const firstSibling = sortedSiblings?.[0];
  const filteredSiblings = sortedSiblings?.filter(
    sibling =>
      sibling.id !==
      (isSortingApplied || isSearchingApplied ? product.id : firstSibling?.id),
  );

  const isFirstSiblingDifferentFromOriginal = firstSibling?.id !== product.id;

  return {
    id: product.id,
    name: product?.menuTitle || formatProductTitle(product.name ?? ''),
    url: `${PRODUCT_URL_PATH}${product.slug}`,
    slug: product?.oldSlug ?? '', // TODO: replace with product.old_slug when available
    path_slug: product.slug ?? '',
    enabled: product.isActive,
    description: product.description,
    category: startCase(product.category?.toLocaleLowerCase()),
    catalogParentId: product.catalogParentId,
    subcategory: product.subtype,
    category_id: product.category,

    thc: getLabValue(product.thc),
    cbd: getLabValue(product.cbd),

    qty: product?.availableUnits,
    brand_name: product.brand,
    brand_id: product.brand,
    brand_subtype: startCase(product.name?.toLocaleLowerCase()),
    flavors: product.flavors,
    effects: product.effects,
    generals: product.generals,
    ingredients: product.ingredients,
    in_stock: inStock,
    sales_price: product.productData?.pricing?.priceSell ?? 0 * 100,
    flower_type: startCase(product.classification?.toLocaleLowerCase()),
    cannabis_type: product.classification as CannabisType,
    available_for_delivery: true, // not available in sell treez at the moment
    available_for_pickup: true, // not available in sell treez at the moment
    asset: assets[0],
    assets: assets,
    price_type: trezPriceType as PriceType,
    variants: mapVariants(product),
    discounts: mappedDiscounts,
    labResults: mapLabResults(product?.productData?.labResults),
    isOutStock: outStock,
    isLowStock: !product.isAboveThreshold,
    base_weight: product?.amount,
    tier_method: product?.productData?.pricing?.tierMethod as TierMethod,
    pricing: mapPricing(product?.productData?.pricing),
    size: product?.customSize ?? '',

    isHideFromMenu: product?.isHideFromMenu,

    ...(isFirstSiblingDifferentFromOriginal &&
    !(isSortingApplied || isSearchingApplied)
      ? {
          ...firstSibling,
        }
      : {}),

    siblings: filteredSiblings ?? [],
  };
};

const mapSiblings = (
  timeZone: string,
  siblings?: Partial<OpenSearchProduct>[],
  fulfillmentType?: FulfillmentType,
): Partial<GProduct>[] => {
  if (!siblings?.length) return [];

  return siblings
    ?.sort((a, b) => (a.amount ?? 0) - (b.amount ?? 0))
    ?.map(sibling => {
      const openSearchSibling = sibling as OpenSearchProduct;
      const {
        mappedDiscounts,
        appliedDiscountIds,
        minDiscountedPrice,
        discountAmount,
        discountPercent,
      } = computeDiscountData({
        product: openSearchSibling,
        timeZone,
        fulfillmentType,
      });

      // Match parent mapProduct: apply fulfillment-aware pricing before mapVariants
      if (openSearchSibling.productData?.pricing) {
        openSearchSibling.productData.pricing.discountedPrice =
          minDiscountedPrice;
        openSearchSibling.productData.pricing.discountAmount = discountAmount;
        openSearchSibling.productData.pricing.discountPercent = discountPercent;
        openSearchSibling.productData.pricing.appliedDiscountIds =
          appliedDiscountIds;
      }

      const siblingAssets = mapPhotoAssets(openSearchSibling);

      return {
        amount: `${sibling.amount ?? 0}`,
        base_weight: sibling.amount ?? 0,
        id: sibling.id,
        name: sibling?.menuTitle || formatProductTitle(sibling.name ?? ''),
        url: `${PRODUCT_URL_PATH}${sibling.slug}`,
        slug: sibling?.oldSlug ?? '', // TODO: replace with product.old_slug when available
        path_slug: sibling.slug ?? '',
        size: sibling?.customSize ?? '',
        pricing: mapPricing(openSearchSibling.productData?.pricing),
        variants: mapVariants(openSearchSibling),
        discounts: mappedDiscounts,
        asset: siblingAssets[0],
        assets: siblingAssets,
        isOutStock: sibling?.availableUnits === 0,
        isLowStock: !sibling?.isAboveThreshold,
      };
    });
};

const mapPricing = (pricing?: OpenSearchProductPricing): Pricing => {
  if (!pricing) {
    return {
      price_type: 'FLAT',
      tier_method: 'FLAT',
      tier_name: '',
      discount_amount: 0,
      discount_percent: 0,
      tier_pricing_detail: [],
      applied_discount_ids: [],
      discount_method: '',
    };
  }

  return {
    price_type: pricing?.priceType as PriceType,
    tier_method: pricing?.tierMethod as TierMethod,
    tier_name: pricing?.tierName ?? '',
    discount_amount: pricing?.discountAmount ?? 0,
    discount_percent: pricing?.discountPercent ?? 0,
    tier_pricing_detail:
      pricing?.tierDetails?.map(t => ({
        start_value: t.startValue ?? 0,
        price_per_value: t.pricePerValue ?? 0,
      })) ?? [],
    applied_discount_ids: pricing?.appliedDiscountIds ?? [],
    discount_method: pricing?.discountMethod ?? '',
  };
};

const mapLabResults = (
  labResults?: OpenSearchProductLabResult[],
): LabResult[] => {
  if (!labResults?.length) {
    return [];
  }

  return labResults?.map(l => ({
    result_type: l.type ?? '',
    amount: {
      minimum_value: l.amount?.minValue ?? '',
      maximum_value: l.amount?.maxValue ?? '',
    },
    amount_type: l.amountType ?? '',
  }));
};

const mapDiscounts = (discounts?: OpenSearchProductDiscount[]): Discount[] => {
  if (!discounts?.length) {
    return [];
  }

  return discounts?.map(d => ({
    discount_id: d.discountId ?? '',
    discount_title: d.discountTitle ?? '',
    discount_display_title: d.discountDisplayTitle ?? '',
    discount_affinity: d.discountAffinity ?? '',
    discount_amount: d.discountAmount ?? 0,
    discount_method: d.discountMethod ?? '',
    discount_stackable: d.discountStackable ?? '',
  }));
};

const getSalePrice = (discountedPrice: string | null, price: number): number =>
  discountedPrice ? calculatePrice(discountedPrice) : price;

const calculatePrice = (value: string): number => {
  const parsedValue = parseFloat(value);

  if (isNaN(parsedValue)) return 0;

  // Round to 2 decimal places and convert to string
  const roundedValue = parsedValue?.toFixed(2);

  // Remove the decimal point and convert to number
  const cents = parseInt(roundedValue?.replace('.', ''), 10);

  return cents;
};

const calculateSalePrice = (
  pricePerValue: number,
  startValue: number,
  discountPercent?: number,
): number => {
  if (isNaN(pricePerValue) || isNaN(startValue)) return 0;

  const calculatedDiscountPercentage = discountPercent
    ? discountPercent / 100
    : 0;

  // Round the per-unit discount to cents first so tier math cannot leave fractional cents
  // (e.g. $8.57 @ 20% → $1.71 off → $6.86 → 686¢, not 685.6).
  const roundedDiscountAmount =
    Math.round(pricePerValue * calculatedDiscountPercentage * 100) / 100;

  return calculatePrice(
    String((pricePerValue - roundedDiscountAmount) * startValue),
  );
};

const mapVariants = (product: OpenSearchProduct): Variant[] => {
  const p = product?.productData;
  const priceType = p?.pricing?.priceType ?? 'FLAT';

  if (priceType === 'FLAT') {
    return [mapVariant(product)];
  }

  if (priceType === 'TIER') {
    const t = p?.pricing?.tierDetails
      ?.filter(t => t?.startValue && t?.startValue > 0) // skip the first tier
      .map(t => mapTierVariant(product, t));

    return t ?? [];
  }

  return [];
};

const mapVariant = (product: OpenSearchProduct): Variant => {
  const price = calculatePrice(String(product.productData?.pricing?.priceSell));
  const salePrice = getSalePrice(
    String(product.productData?.pricing?.discountedPrice),
    price,
  );

  const discount_amount = calculatePrice(
    String(product?.productData?.pricing?.discountAmount ?? '0'),
  );
  const discount_percent = parseFloat(
    product?.productData?.pricing?.discountPercent?.toFixed(2) ?? '0',
  );

  return {
    id: product.id,
    name: product.name,
    pos_id: product.id,
    price: price,
    salePrice: salePrice,
    discount_amount,
    discount_percent,
    type: mapVariantType(product),
  };
};

const mapTierVariant = (
  product: OpenSearchProduct,
  tierPricingDetail: OpenSearchTierDetail,
): Variant => {
  // tier base price
  const price = calculatePrice(
    String(
      (tierPricingDetail.pricePerValue ?? 0) *
        (tierPricingDetail.startValue ?? 0),
    ),
  );

  const discount_amount = calculatePrice(
    String(product?.productData?.pricing?.discountAmount ?? '0'),
  );
  const discount_percent = parseFloat(
    product?.productData?.pricing?.discountPercent?.toFixed(2) ?? '0',
  );
  const discountMethod = product?.productData?.pricing?.discountMethod ?? '';

  const salePrice =
    discountMethod === 'PERCENT'
      ? calculateSalePrice(
          tierPricingDetail.pricePerValue ?? 0,
          tierPricingDetail.startValue ?? 0,
          discount_percent,
        )
      : Math.max(0, price - discount_amount);

  return {
    id: `${product.id}-${tierPricingDetail.startValue}`, // we are adding the weight to the id to make it unique
    name: product.name,
    pos_id: product.id,
    price: price,
    salePrice: salePrice,
    discount_amount,
    discount_percent,
    type: mapVariantType({
      ...product,
      amount: tierPricingDetail.startValue ?? 0,
    }),
  };
};

const mapVariantType = (product: OpenSearchProduct): string => {
  if (
    product.unitOfMeasurement === 'MILLIGRAMS' ||
    product.unitOfMeasurement === 'UNIT'
  ) {
    return 'each';
  }

  if (product.unitOfMeasurement === 'OUNCES') {
    switch (product.amount) {
      case 0.125:
        return 'eighth_ounce';
      case 0.25:
        return 'quarter_ounce';
      case 0.5:
        return 'half_ounce';
      case 1:
        return 'ounce';
      default:
        return `${product.amount}_${product.unitOfMeasurement?.toLowerCase()}`;
    }
  }

  switch (product.amount) {
    case 0.5:
      return 'half_gram';
    case 1:
      return 'gram';
    case 2:
      return 'two_gram';
    case 3.5:
      return 'eighth_ounce';
    case 7:
      return 'quarter_ounce';
    case 14:
      return 'half_ounce';
    case 28:
      return 'ounce';
    default:
      return product.amount && product.unitOfMeasurement
        ? `${product.amount}_${product.unitOfMeasurement?.toLowerCase()}`
        : '';
  }
};
// For a BOGO or BUNDLE that has not met it’s schedule condition, the API returns discount_affinity=cart.
// This is actually a bug that should be fixed, as discount_affinity should
// be “pre-cart” for a bogo. However because this bug is consistent,
// we can build logic around it for a short-term
// fix until we add API logic to filter out inactive BOGOS and BUNDLES from the response.
const filterDiscountsOutOfScheduleAndByFulfillmentType = ({
  discounts,
  timeZone,
  fulfillmentType,
}: {
  discounts?: OpenSearchProductDiscount[];
  timeZone?: string;
  fulfillmentType?: FulfillmentType;
}): OpenSearchProductDiscount[] => {
  if (!discounts?.length) {
    return [];
  }

  const methodsToFilter = ['BOGO', 'BUNDLE'];

  return discounts
    .map(discount => {
      if (discount?.discountAffinity === 'EVAL') {
        return {
          ...discount,
          discountAffinity: isDiscountActive(discount, timeZone!)
            ? 'Pre-Cart'
            : 'Cart',
        };
      }
      if (isFulfillmentTypeDiscount(discount)) {
        if (!fulfillmentType) return null;

        const discountCondition = discount?.discountConditions?.find(
          condition => condition?.type === 'Fulfillment Type',
        );
        const fulfillmentTypeValues = discountCondition?.value
          ?.split(',')
          .map(value => value.trim().toUpperCase());

        if (
          !!fulfillmentTypeValues?.length &&
          !fulfillmentTypeValues.includes(fulfillmentType)
        ) {
          return null;
        }

        return {
          ...discount,
          discountAffinity: 'Pre-Cart',
        };
      }
      return discount;
    })
    .filter(discount => {
      if (!discount) return false;

      const method = discount?.discountMethod;

      const isSpecialCase = methodsToFilter.includes(method ?? '');

      if (!isSpecialCase) return true;

      return discount?.discountAffinity !== 'Cart';
    }) as OpenSearchProductDiscount[];
};

const isFulfillmentTypeDiscount = (
  discount: OpenSearchProductDiscount,
): boolean =>
  discount?.discountConditions?.length === 1 &&
  discount?.discountConditions?.[0]?.type === 'Fulfillment Type';

const isDiscountActive = (
  discount: OpenSearchProductDiscount,
  timeZone: string,
): boolean => {
  if (discount?.discountConditions?.length === 1) {
    return isScheduleDiscountActive(
      discount?.discountConditions?.[0],
      timeZone,
    );
  }

  if (discount?.discountConditions?.length === 2) {
    return (
      isScheduleDiscountActive(discount?.discountConditions?.[0], timeZone) ||
      isScheduleDiscountActive(discount?.discountConditions?.[1], timeZone)
    );
  }
  return false;
};

const isScheduleDiscountActive = (
  condition: OpenSearchDiscountCondition,
  timeZone: string,
): boolean => {
  if (condition?.scheduleCondition && condition?.type === 'Schedule') {
    return isScheduleActive(condition?.scheduleCondition, timeZone);
  }
  return false;
};

function isScheduleActive(
  schedule: OpenSearchScheduleCondition,
  timeZone: string,
): boolean {
  const now = new Date();
  const zonedNow = toZonedTime(now, timeZone);

  if (!schedule.type || !schedule.startDate || !schedule.endDate) {
    return false;
  }

  const zonedStartDate = new Date(schedule.startDate);
  const zonedEndDate = new Date(schedule.endDate);
  // --- Logic for DO_NOT type ---
  if (schedule.type === 'DO_NOT') {
    return (
      !isBefore(zonedNow, zonedStartDate) && !isAfter(zonedNow, zonedEndDate)
    );
  }

  // --- Logic for DAY type ---
  if (schedule.type === 'DAY') {
    if (isBefore(startOfDay(zonedNow), startOfDay(zonedStartDate))) {
      return false;
    }

    if (differenceInHours(zonedEndDate, zonedStartDate) >= 24) {
      return true;
    }

    const startTime = format(zonedStartDate, 'HH:mm:ss', { timeZone });
    const endTime = format(zonedEndDate, 'HH:mm:ss', { timeZone });
    const nowTime = format(zonedNow, 'HH:mm:ss', { timeZone });

    if (endTime < startTime) {
      // Overnight
      return nowTime >= startTime || nowTime <= endTime;
    }
    // Same day
    return nowTime >= startTime && nowTime <= endTime;
  }

  // --- Logic for WEEK type ---
  if (schedule.type === 'WEEK') {
    if (isBefore(startOfDay(zonedNow), startOfDay(zonedStartDate))) {
      return false;
    }
    const targetDay = getDay(zonedStartDate); // 0 = Sunday, 1 = Monday...
    const duration = zonedEndDate.getTime() - zonedStartDate.getTime();

    const thisWeekAnchor = setDay(zonedNow, targetDay);
    const thisWeekStart = new Date(
      thisWeekAnchor.getFullYear(),
      thisWeekAnchor.getMonth(),
      thisWeekAnchor.getDate(),
      zonedStartDate.getHours(),
      zonedStartDate.getMinutes(),
    );
    const thisWeekEnd = new Date(thisWeekStart.getTime() + duration);
    if (!isBefore(zonedNow, thisWeekStart) && !isAfter(zonedNow, thisWeekEnd))
      return true;

    const lastWeekAnchor = setDay(addWeeks(zonedNow, -1), targetDay);
    const lastWeekStart = new Date(
      lastWeekAnchor.getFullYear(),
      lastWeekAnchor.getMonth(),
      lastWeekAnchor.getDate(),
      zonedStartDate.getHours(),
      zonedStartDate.getMinutes(),
    );
    const lastWeekEnd = new Date(lastWeekStart.getTime() + duration);
    if (!isBefore(zonedNow, lastWeekStart) && !isAfter(zonedNow, lastWeekEnd))
      return true;
  }

  // --- Logic for WEEK_DAY type ---
  if (schedule.type === 'WEEK_DAY') {
    if (isBefore(startOfDay(zonedNow), startOfDay(zonedStartDate))) {
      return false;
    }
    const duration = zonedEndDate.getTime() - zonedStartDate.getTime();

    let mostRecentWeekday = zonedNow;
    if (getDay(zonedNow) === 6) {
      // Saturday
      mostRecentWeekday = addDays(zonedNow, -1);
    } else if (getDay(zonedNow) === 0) {
      // Sunday
      mostRecentWeekday = addDays(zonedNow, -2);
    }

    const recurrenceStart = new Date(
      mostRecentWeekday.getFullYear(),
      mostRecentWeekday.getMonth(),
      mostRecentWeekday.getDate(),
      zonedStartDate.getHours(),
      zonedStartDate.getMinutes(),
    );
    const recurrenceEnd = new Date(recurrenceStart.getTime() + duration);

    if (
      !isBefore(zonedNow, recurrenceStart) &&
      !isAfter(zonedNow, recurrenceEnd)
    ) {
      return true;
    }
  }

  // --- Logic for MONTH type ---
  if (schedule.type === 'MONTH') {
    if (isBefore(startOfDay(zonedNow), startOfDay(zonedStartDate))) {
      return false;
    }
    const targetDayOfWeek = getDay(zonedStartDate);
    const targetOrdinal = Math.floor((getDate(zonedStartDate) - 1) / 7) + 1;
    const duration = zonedEndDate.getTime() - zonedStartDate.getTime();

    const firstDayOfThisMonth = startOfMonth(zonedNow);
    let firstTargetDayOfThisMonth = firstDayOfThisMonth;
    while (getDay(firstTargetDayOfThisMonth) !== targetDayOfWeek) {
      firstTargetDayOfThisMonth = addDays(firstTargetDayOfThisMonth, 1);
    }
    const thisMonthAnchorDate = addDays(
      firstTargetDayOfThisMonth,
      (targetOrdinal - 1) * 7,
    );

    if (getMonth(thisMonthAnchorDate) === getMonth(zonedNow)) {
      const thisMonthStart = new Date(
        thisMonthAnchorDate.getFullYear(),
        thisMonthAnchorDate.getMonth(),
        thisMonthAnchorDate.getDate(),
        zonedStartDate.getHours(),
        zonedStartDate.getMinutes(),
      );
      const thisMonthEnd = new Date(thisMonthStart.getTime() + duration);
      if (
        !isBefore(zonedNow, thisMonthStart) &&
        !isAfter(zonedNow, thisMonthEnd)
      )
        return true;
    }

    const firstDayOfLastMonth = startOfMonth(addMonths(zonedNow, -1));
    let firstTargetDayOfLastMonth = firstDayOfLastMonth;
    while (getDay(firstTargetDayOfLastMonth) !== targetDayOfWeek) {
      firstTargetDayOfLastMonth = addDays(firstTargetDayOfLastMonth, 1);
    }
    const lastMonthAnchorDate = addDays(
      firstTargetDayOfLastMonth,
      (targetOrdinal - 1) * 7,
    );

    if (getMonth(lastMonthAnchorDate) === getMonth(addMonths(zonedNow, -1))) {
      const lastMonthStart = new Date(
        lastMonthAnchorDate.getFullYear(),
        lastMonthAnchorDate.getMonth(),
        lastMonthAnchorDate.getDate(),
        zonedStartDate.getHours(),
        zonedStartDate.getMinutes(),
      );
      const lastMonthEnd = new Date(lastMonthStart.getTime() + duration);
      if (
        !isBefore(zonedNow, lastMonthStart) &&
        !isAfter(zonedNow, lastMonthEnd)
      )
        return true;
    }
  }

  // --- Logic for MONTH_DAY type ---
  if (schedule.type === 'MONTH_DAY') {
    if (isBefore(startOfDay(zonedNow), startOfDay(zonedStartDate))) {
      return false;
    }
    const targetDayOfMonth = getDate(zonedStartDate);
    const duration = zonedEndDate.getTime() - zonedStartDate.getTime();

    try {
      const thisMonthAnchor = setDate(zonedNow, targetDayOfMonth);
      const thisMonthStart = new Date(
        thisMonthAnchor.getFullYear(),
        thisMonthAnchor.getMonth(),
        thisMonthAnchor.getDate(),
        zonedStartDate.getHours(),
        zonedStartDate.getMinutes(),
      );
      const thisMonthEnd = new Date(thisMonthStart.getTime() + duration);
      if (
        !isBefore(zonedNow, thisMonthStart) &&
        !isAfter(zonedNow, thisMonthEnd)
      ) {
        return true;
      }
    } catch (e) {
      console.error('Error in MONTH_DAY schedule:', e);
    }

    try {
      const lastMonthAnchor = setDate(
        addMonths(zonedNow, -1),
        targetDayOfMonth,
      );
      const lastMonthStart = new Date(
        lastMonthAnchor.getFullYear(),
        lastMonthAnchor.getMonth(),
        lastMonthAnchor.getDate(),
        zonedStartDate.getHours(),
        zonedStartDate.getMinutes(),
      );
      const lastMonthEnd = new Date(lastMonthStart.getTime() + duration);
      if (
        !isBefore(zonedNow, lastMonthStart) &&
        !isAfter(zonedNow, lastMonthEnd)
      )
        return true;
    } catch (e) {
      console.error('Error in MONTH_DAY schedule:', e);
    }
  }

  // --- Logic for ANNUAL type ---
  if (schedule.type === 'ANNUAL') {
    if (isBefore(startOfDay(zonedNow), startOfDay(zonedStartDate))) {
      return false;
    }
    const targetMonth = getMonth(zonedStartDate);
    const targetDay = getDate(zonedStartDate);
    const duration = zonedEndDate.getTime() - zonedStartDate.getTime();

    try {
      const thisYearAnchor = setDate(
        setMonth(zonedNow, targetMonth),
        targetDay,
      );
      const thisYearStart = new Date(
        thisYearAnchor.getFullYear(),
        thisYearAnchor.getMonth(),
        thisYearAnchor.getDate(),
        zonedStartDate.getHours(),
        zonedStartDate.getMinutes(),
      );
      const thisYearEnd = new Date(thisYearStart.getTime() + duration);
      if (!isBefore(zonedNow, thisYearStart) && !isAfter(zonedNow, thisYearEnd))
        return true;
    } catch (e) {
      console.error('Error in ANNUAL schedule:', e);
    }

    try {
      const lastYearAnchor = setDate(
        setMonth(addYears(zonedNow, -1), targetMonth),
        targetDay,
      );
      const lastYearStart = new Date(
        lastYearAnchor.getFullYear(),
        lastYearAnchor.getMonth(),
        lastYearAnchor.getDate(),
        zonedStartDate.getHours(),
        zonedStartDate.getMinutes(),
      );
      const lastYearEnd = new Date(lastYearStart.getTime() + duration);
      if (!isBefore(zonedNow, lastYearStart) && !isAfter(zonedNow, lastYearEnd))
        return true;
    } catch (e) {
      console.error('Error in ANNUAL schedule:', e);
    }
  }

  // --- Logic for CUSTOM type ---
  if (schedule.type === 'CUSTOM') {
    if (!schedule.repeat.intervalType || !schedule.repeat.intervalCount)
      return false;
    if (isBefore(zonedNow, zonedStartDate)) return false;
    if (
      schedule.repeat.end &&
      isAfter(
        startOfDay(zonedNow),
        startOfDay(fromZonedTime(schedule.repeat.end, timeZone)),
      )
    )
      return false;

    const duration = zonedEndDate.getTime() - zonedStartDate.getTime();
    let occurrenceCount = 0;
    let cursor = zonedStartDate;

    while (!isAfter(cursor, zonedNow)) {
      let isValidRecurrenceDay = false;

      if (schedule.repeat.intervalType.toLowerCase() === 'day') {
        const daysBetween = differenceInDays(cursor, zonedStartDate);
        if (daysBetween % schedule.repeat.intervalCount === 0)
          isValidRecurrenceDay = true;
      } else if (schedule.repeat.intervalType.toLowerCase() === 'week') {
        const weeksBetween = differenceInWeeks(
          startOfWeek(cursor),
          startOfWeek(zonedStartDate),
        );
        if (weeksBetween % schedule.repeat.intervalCount === 0) {
          const dayName = format(cursor, 'EEEE');
          if (schedule.repeat.days?.includes(dayName)) {
            isValidRecurrenceDay = true;
          }
        }
      } else if (schedule.repeat.intervalType.toLowerCase() === 'month') {
        const monthsBetween = differenceInMonths(
          startOfMonth(cursor),
          startOfMonth(zonedStartDate),
        );
        if (
          monthsBetween % schedule.repeat.intervalCount === 0 &&
          getDate(cursor) === getDate(zonedStartDate)
        ) {
          isValidRecurrenceDay = true;
        }
      } else if (schedule.repeat.intervalType.toLowerCase() === 'year') {
        const yearsBetween = differenceInYears(
          startOfYear(cursor),
          startOfYear(zonedStartDate),
        );
        if (
          yearsBetween % schedule.repeat.intervalCount === 0 &&
          getMonth(cursor) === getMonth(zonedStartDate) &&
          getDate(cursor) === getDate(zonedStartDate)
        ) {
          isValidRecurrenceDay = true;
        }
      }

      if (isValidRecurrenceDay) {
        occurrenceCount++;
        const eventStart = new Date(
          cursor.getFullYear(),
          cursor.getMonth(),
          cursor.getDate(),
          zonedStartDate.getHours(),
          zonedStartDate.getMinutes(),
        );
        const eventEnd = new Date(eventStart.getTime() + duration);
        if (!isBefore(zonedNow, eventStart) && !isAfter(zonedNow, eventEnd)) {
          if (
            schedule.repeat.occurrences &&
            occurrenceCount > schedule.repeat.occurrences
          ) {
            return false;
          }
          return true;
        }
      }
      cursor = addDays(cursor, 1);
    }
  }

  return false;
}

const WEIGHT_TIER_METHODS = ['WEIGHT_GRAMS', 'BULK_WEIGHT'];

const priceType = (product: OpenSearchProduct): PriceType => {
  const pricing = product?.productData?.pricing;

  if (
    pricing?.priceType === 'TIER' &&
    WEIGHT_TIER_METHODS.includes(pricing?.tierMethod ?? '')
  ) {
    return PriceType.Tier;
  }

  return PriceType.Flat;
};

const mapPhotoAssets = (product: OpenSearchProduct): Asset[] => {
  if (product?.productData?.images?.length) {
    return product.productData.images.map(image => ({ image }));
  }

  return [generateOpenSearchStockImage(product)];
};

const getStockItem = (stockList, type, subType) => {
  if (!stockList?.length) return null;

  const formattedSub = subType?.toLowerCase()?.trim();
  const formattedType = type?.toLowerCase()?.trim();

  const getStockMatch = (searchName: string) => {
    if (!searchName) return null;

    return (
      stockList?.find(
        stock => stock?.name?.toLowerCase().trim() === searchName,
      ) || null
    );
  };

  //First search for the format 'type:subtype'
  if (formattedType && formattedSub) {
    const param = `${formattedType}:${formattedSub}`;
    const subtypeMatch = getStockMatch(param);

    if (subtypeMatch) return subtypeMatch;
  }

  // Then search by type only
  if (formattedType) {
    const typeMatch = getStockMatch(formattedType);
    if (typeMatch) return typeMatch;
  }

  return null;
};

const generateOpenSearchStockImage = (product: OpenSearchProduct) => {
  const productType = (data as unknown as IStoreMetadata)?.productCard;

  if (productType) {
    const stockImage = getStockItem(
      productType?.stockImage,
      product.category,
      product.subtype,
    );

    if (stockImage) {
      return {
        image: stockImage?.default_product_image?.url,
        stock: true,
      };
    }
  }

  return {
    image: PLACEHOLDER,
    stock: true,
  };
};

const getProductsFromOpenSearchResponse = ({
  result,
  isSortingApplied,
  isSearchingApplied,
  timeZone,
  fulfillmentType,
}: {
  result: ResponseBody & { _timeZone?: string };
  isSortingApplied?: boolean;
  isSearchingApplied?: boolean;
  timeZone?: string;
  fulfillmentType?: FulfillmentType;
}): GProduct[] => {
  const effectiveTimeZone = timeZone ?? result?._timeZone;
  const products = (result?.hits?.hits ?? []).map(({ _source, inner_hits }) =>
    mapProduct({
      product: {
        ..._source,
        siblings: inner_hits?.siblings?.hits?.hits?.map(
          sibling => sibling._source,
        ),
      } as OpenSearchProduct,
      isSortingApplied,
      isSearchingApplied,
      timeZone: effectiveTimeZone,
      fulfillmentType,
    }),
  );

  return products;
};

export { mapProduct, getProductsFromOpenSearchResponse };
